import React, { useEffect, useState } from 'react';
import { Card, Row, Col, Button, Container, Badge, Modal } from 'react-bootstrap';
import { showTemplates, syncTemplates, getCompany, deleteTemplate } from '../../../helpers';
import './TemplateListPage.css';
import ViewTemplate from './ViewTemplate';
import { Eye, PencilSquare, Trash } from 'react-bootstrap-icons';

interface Template {
    template_id: number;
    template_name: string;
    category: string;
    language: string;
    wa_approval_status: string;
    status: string;
    created_at: string;
    title?: string;
    title_text?: string;
    title_type?: string;
    title_file?: string;
    body: string;
    footer?: string;
    buttons?: any[] | null;
    header_variables?: string[];
    variables?: string[];
}

const TemplateListPage: React.FC = () => {
    const [templates, setTemplates] = useState<Template[]>([]);
    const [loading, setLoading] = useState<boolean>(true);
    const [error, setError] = useState<string | null>(null);
    const [syncLoading, setSyncLoading] = useState(false);
    const [companyName, setCompanyName] = useState<string>('Loading...');
    const [showTemplateModal, setShowTemplateModal] = useState(false);
    const [selectedTemplateId, setSelectedTemplateId] = useState<number | null>(null);
    const [filteredTemplates, setFilteredTemplates] = useState<Template[]>([]);
    const [activeCategory, setActiveCategory] = useState<string>("All");
    const [categoryCounts, setCategoryCounts] = useState<{ [key: string]: number }>({});


    const handleSync = async () => {
        setSyncLoading(true);

        try {
            const company_id = JSON.parse(sessionStorage.getItem('shreyu_user') || '{}')?.user?.company_id;

            const result = await syncTemplates(company_id);

            console.log("Sync completed:", result);

            // Re-fetch templates automatically
            const refreshed = await showTemplates();
            const normalizedTemplates = refreshed.data.templates.map((t: Template) => ({
                ...t,
                buttons: Array.isArray(t.buttons) ? t.buttons : []
            }));

            setTemplates(normalizedTemplates);
        } catch (error) {
            console.error("Sync failed:", error);
            alert("Template sync failed");
        } finally {
            setSyncLoading(false);
        }
    };

    const handleDelete = async (id: number) => {
        if (!window.confirm("Are you sure you want to delete this template?")) return;

        try {
            const response = await deleteTemplate(id);

            if (response.data.success) {
                setTemplates(prev => prev.filter(t => t.template_id !== id));
                setFilteredTemplates(prev => prev.filter(t => t.template_id !== id));
            } else {
                alert("Failed to delete template.");
            }
        } catch (error) {
            console.error("Delete error:", error);
            alert("Error deleting template");
        }
    };


    useEffect(() => {
        const fetchTemplates = async () => {
            try {
                const response = await showTemplates();

                const normalizedTemplates = response.data.templates.map((template: Template) => ({
                    ...template,
                    buttons: Array.isArray(template.buttons) ? template.buttons : []
                }));
                setTemplates(normalizedTemplates);
                setFilteredTemplates(normalizedTemplates);

                const counts: { [key: string]: number } = {
                    All: normalizedTemplates.length,
                    Marketing: 0,
                    Utility: 0,
                    Authentication: 0
                };

                normalizedTemplates.forEach((t: any) => {
                    const category = (t.category || "").toLowerCase();

                    if (category === "marketing") counts.Marketing++;
                    else if (category === "utility") counts.Utility++;
                    else if (category === "authentication") counts.Authentication++;
                });

                setCategoryCounts(counts);
                setLoading(false);
            } catch (err) {
                setError('Failed to fetch templates');
                setLoading(false);
                console.error('Error fetching templates:', err);
            }
        };

        fetchTemplates();
    }, []);

    const filterByCategory = (cat: string) => {
        setActiveCategory(cat);

        if (cat === "All") {
            setFilteredTemplates(templates);
        } else {
            setFilteredTemplates(
                templates.filter(t =>
                    t.category?.toLowerCase() === cat.toLowerCase()
                )
            );
        }
    };

    useEffect(() => {
        const fetchCompany = async () => {
            try {
                const company_id = JSON.parse(sessionStorage.getItem('shreyu_user') || '{}')?.user?.company_id;

                const response = await getCompany(company_id);
                setCompanyName(response.data.company_name || 'Company Name');
            } catch (err) {
                console.error('Failed to fetch company:', err);
                setCompanyName('Company Name');
            }
        };

        fetchCompany();
    }, []);
    const renderPreviewText = (text: string, variables: string[] = []) => {
        if (!text) return '';
        let result = text;

        // Replace variable placeholders
        variables.forEach((v, i) => {
            const regex = new RegExp(escapeRegExp(v), 'g');
            result = result.replace(regex, `[Variable ${i + 1}]`);
        });

        // Apply formatting for *bold*, _italic_, and ~strikethrough~
        result = result
            .replace(/\*(.*?)\*/g, '<strong>$1</strong>')
            .replace(/_(.*?)_/g, '<em>$1</em>')
            .replace(/~(.*?)~/g, '<span style="text-decoration: line-through">$1</span>');

        // ✅ Handle all newline cases (\n, \\n, \r\n)
        result = result.replace(/\\n/g, '<br/>').replace(/\n/g, '<br/>');

        return result;
    };


    const escapeRegExp = (string: string) => {
        return string.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    };

    const renderButtonPreview = (button: any, index: number) => {
        if (!button) return null;

        const { types, text, url } = button;

        return (
            <div key={index} className="wa-button-wrapper">
                {types === 'PHONE_NUMBER' && (
                    <b style={{ color: "#1E90FF" }}>
                        {text || 'Call'}
                    </b>
                )}

                {types === 'URL' && (
                    <b style={{ color: "#1E90FF" }}>
                        {text || 'Visit Site'}
                    </b>
                )}

                {types === 'QUICK_REPLY' && (
                    <b style={{ color: "#1E90FF" }}>
                        {text || 'Quick Reply'}
                    </b>
                )}
            </div>
        );
    };


    const renderHeaderPreview = (template: Template) => {

        if (!template.title_type || template.title_type === 'none') return null;

        switch (template.title_type.toLowerCase()) {
            case 'text':
                return (
                    <div className="template-text-header">
                        <div dangerouslySetInnerHTML={{
                            __html: renderPreviewText(template.title || '', template.header_variables || [])
                        }} />
                    </div>
                );
            case 'image':
                return (
                    <div className="template-image-header">
                        <div className="template-image-placeholder">
                            <img
                                src={`https://mercury.skynyxserver.com/git_projects/WA-skynyx/file_storage/template/${template.title_file}`}
                                alt=""
                                style={{ width: "50%", height: "50%", borderRadius: "6px" }}
                            />
                        </div>
                    </div>
                );
            case 'video':
                return (
                    <div className="template-video-header">
                        <div className="template-video-placeholder">[Video Header]</div>
                    </div>
                );
            case 'document':
                return (
                    // <a href="/files/mydoc.pdf" download>
                    <div className="template-document-header">
                        <div className="template-document-placeholder">
                            <svg width="24" height="24" fill="currentColor" viewBox="0 0 24 24">
                                <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                                <polyline points="14 2 14 8 20 8" />
                            </svg>
                        </div>
                    </div>
                    // </a>

                );
            default:
                return null;
        }
    };

    const getStatusBadge = (status: string) => {
        if (!status) return <Badge bg="secondary">Unknown</Badge>;

        switch (status.toLowerCase()) {
            case 'approved':
                return <Badge bg="success">Approved</Badge>;
            case 'pending':
                return <Badge bg="warning">Pending</Badge>;
            case 'rejected':
                return <Badge bg="danger">Rejected</Badge>;
            default:
                return <Badge bg="secondary">{status}</Badge>;
        }
    };

    if (loading) {
        return <div className="text-center py-5">Loading templates...</div>;
    }

    if (error) {
        return <div className="alert alert-danger">{error}</div>;
    }

    if (!templates || templates.length === 0) {
        return <Button
            variant="primary"
            onClick={handleSync}
            disabled={syncLoading}
        >
            {syncLoading ? "Syncing..." : "Sync Templates"}
        </Button>;
    }



    return (
        // <Container fluid className="border border-2" >
        <>
            <Card >
                <Card.Header className='bg-secondary text-white'>
                    <div className="d-flex justify-content-between align-items-center">
                        <h3 className='text-white'>Templates</h3>
                        <Button
                            variant="primary"
                            onClick={handleSync}
                            disabled={syncLoading}
                        >
                            {syncLoading ? "Syncing..." : "Sync Templates"}
                        </Button>

                    </div>
                </Card.Header>
                <Card.Body>
                    <div className="mb-4 text-center">

                        <Button
                            variant={activeCategory === "All" ? "secondary" : "outline-secondary"}
                            className="me-2 mb-2"
                            size="sm"
                            onClick={() => filterByCategory("All")}
                        >
                            All ({categoryCounts.All || 0})
                        </Button>

                        <Button
                            variant={activeCategory === "marketing" ? "secondary" : "outline-secondary"}
                            className="me-2 mb-2"
                            size="sm"
                            onClick={() => filterByCategory("marketing")}
                        >
                            Marketing ({categoryCounts.Marketing || 0})
                        </Button>

                        <Button
                            variant={activeCategory === "utility" ? "secondary" : "outline-secondary"}
                            className="me-2 mb-2"
                            size="sm"
                            onClick={() => filterByCategory("utility")}
                        >
                            Utility ({categoryCounts.Utility || 0})
                        </Button>

                        <Button
                            variant={activeCategory === "authentication" ? "secondary" : "outline-secondary"}
                            className="me-2 mb-2"
                            size="sm"
                            onClick={() => filterByCategory("authentication")}
                        >
                            Authentication ({categoryCounts.Authentication || 0})
                        </Button>

                    </div>
                    <Row xs={1} md={2} lg={3} className="g-4">
                        {filteredTemplates.map((template) => (
                            <Col key={template.template_id}>
                                <Card className="h-100 template-card">
                                    <Card.Header className="bg-secondary d-flex justify-content-between align-items-center">
                                        <div className="d-flex justify-content-between align-items-center">
                                            <h5 className="mb-0 text-white">{template.template_name || 'Untitled Template'}</h5>
                                            {getStatusBadge(template.wa_approval_status)}
                                        </div>
                                        <div >
                                            {template.category || 'No category'} • {template.language || 'No language'}
                                        </div>
                                    </Card.Header>
                                    <Card.Body>
                                        <div className="template-preview-container">
                                            <div className="template-preview">
                                                <div className="template-header-preview">
                                                    <div className="template-profile-pic">
                                                        <img src="https://mercury.skynyxserver.com/git_projects/old/skynyx.png" alt="" />
                                                    </div>
                                                    <div className="template-contact">
                                                        <div className="template-contact-name text-white">{companyName}</div>
                                                        <div className="template-contact-status">online</div>
                                                    </div>
                                                </div>
                                                <div className="template-message-preview">
                                                    {renderHeaderPreview(template)}
                                                    <div className="template-body-preview pl-2" style={{ paddingTop: "6px", paddingBottom: "6px", borderRadius: "6px", paddingLeft: "11px", paddingRight: "11px" }}  >
                                                        <div dangerouslySetInnerHTML={{
                                                            __html: renderPreviewText(template.body, template.variables || [])
                                                        }} />
                                                    </div>
                                                    {template.footer && (
                                                        <div className="template-footer-preview" style={{ paddingTop: "6px", paddingBottom: "6px", borderRadius: "6px", paddingLeft: "11px", paddingRight: "11px" }}>
                                                            {template.footer}
                                                        </div>
                                                    )}
                                                    {template.buttons && template.buttons.length > 0 && (
                                                        <div className="template-buttons-preview" style={{ paddingTop: "6px", paddingBottom: "6px", borderRadius: "6px", paddingLeft: "11px", paddingRight: "11px" }}>
                                                            {template.buttons.map((button, index) =>
                                                                renderButtonPreview(button, index)
                                                            )}
                                                        </div>
                                                    )}
                                                </div>
                                            </div>
                                        </div>
                                    </Card.Body>
                                    <Card.Footer className="text-muted small">
                                        <div className="d-flex justify-content-center gap-2">
                                        <span>Created: {template.created_at ? new Date(template.created_at).toLocaleDateString() : 'Unknown date'}</span>
                                        <div  className="d-flex justify-content-center gap-2">
                                            <Button
                                                variant="info"
                                                size="sm"
                                                onClick={() => {
                                                    setSelectedTemplateId(template.template_id);
                                                    setShowTemplateModal(true);
                                                }}
                                            >
                                                <Eye /> 
                                            </Button>
                                            <Button
                                                variant="danger"
                                                size="sm"
                                                onClick={() => handleDelete(template.template_id)}
                                            >
                                                <Trash />
                                            </Button>

                                        </div>
                                        </div>
                                    </Card.Footer>
                                </Card>
                            </Col>
                        ))}
                    </Row>
                </Card.Body>
            </Card>
            <Modal show={showTemplateModal} onHide={() => setShowTemplateModal(false)} size="xl">
                <Modal.Header closeButton />
                <Modal.Body>

                    {<ViewTemplate templateId={selectedTemplateId} />}
                </Modal.Body>
            </Modal>

        </>
        // </Container> 

    );
};

export default TemplateListPage;